import {
  Kanban, Zap, BarChart2, SlidersHorizontal, ArrowRight, Check, X, Star,
  AlertCircle, Trophy, Target, TrendingUp, Sparkles, Upload,
  MessageCircle, Rocket, Building2, Megaphone,
} from "lucide-react";
import { Stars, scrollTo } from "./LandingHelpers";
import { RESULTS, SEGMENTS } from "./LandingContent";

export default function LandingSections({ c, lang, handleCTA }) {
  return (
    <>
      {/* COMO FUNCIONA */}
      <section id="como-funciona" className="py-20 sm:py-28 bg-white scroll-mt-16" data-testid="landing-how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-[#3B82F6] text-xs font-semibold px-4 py-1.5 rounded-full border border-blue-100 mb-4">
              <Sparkles className="w-3.5 h-3.5" />{c.howItWorks.badge}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 mb-4">{c.howItWorks.title}</h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">{c.howItWorks.sub}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {c.howItWorks.steps.map((step, i) => (
              <div key={i} className="relative">
                {i < c.howItWorks.steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-[calc(100%-0px)] w-full h-px border-t-2 border-dashed border-slate-200 z-0" style={{left:"calc(50% + 2.5rem)", width:"calc(100% - 5rem)"}} />
                )}
                <div className="relative z-10 flex flex-col items-center text-center p-6 bg-slate-50 rounded-2xl border border-slate-200 hover:border-[#3B82F6] hover:bg-blue-50/30 transition-all group">
                  <div className="w-16 h-16 bg-white border-2 border-slate-200 rounded-2xl flex items-center justify-center mb-4 shadow-sm group-hover:border-[#3B82F6] group-hover:shadow-blue-100 group-hover:shadow-md transition-all">
                    <step.icon className="w-7 h-7 text-[#3B82F6]" strokeWidth={1.5} />
                  </div>
                  <span className="text-xs font-bold text-[#3B82F6] bg-blue-50 border border-blue-100 px-2.5 py-0.5 rounded-full mb-3">{step.n}</span>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <button onClick={handleCTA}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#3B82F6] text-white rounded-xl font-semibold text-base hover:bg-blue-600 transition-all shadow-lg shadow-blue-200 hover:shadow-xl hover:-translate-y-0.5">
              {c.howItWorks.cta} <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-slate-400 mt-3">{c.hero.hint}</p>
          </div>
        </div>
      </section>

      {/* PRODUCT SCREENSHOTS */}
      <section className="py-20 sm:py-28 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-[#3B82F6] text-xs font-semibold px-4 py-1.5 rounded-full border border-blue-100 mb-4">
              <Sparkles className="w-3.5 h-3.5" />
              {lang === "pt-BR" ? "Veja o sistema em a\u00e7\u00e3o" : lang === "en-US" ? "See the system in action" : "Ve el sistema en acci\u00f3n"}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 mb-4">
              {lang === "pt-BR" ? "Uma interface pensada para vender" : lang === "en-US" ? "An interface built to sell" : "Una interfaz dise\u00f1ada para vender"}
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              {lang === "pt-BR" ? "Cada tela foi projetada para que o vendedor perca o m\u00ednimo de tempo poss\u00edvel." : lang === "en-US" ? "Every screen was designed so reps spend the least time possible on the tool." : "Cada pantalla fue dise\u00f1ada para que el vendedor pierda el m\u00ednimo tiempo posible."}
            </p>
          </div>

          {/* Row 1: Kanban */}
          <div className="flex flex-col lg:flex-row items-center gap-12 mb-24">
            <div className="flex-1 order-2 lg:order-1">
              <span className="inline-flex items-center gap-2 text-xs font-bold text-[#3B82F6] bg-blue-50 border border-blue-100 px-3 py-1 rounded-full mb-4">
                <Kanban className="w-3.5 h-3.5" />{lang === "pt-BR" ? "Kanban & Pipeline" : "Kanban & Pipeline"}
              </span>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                {lang === "pt-BR" ? "Visualize o funil inteiro num olhar" : lang === "en-US" ? "See your entire funnel at a glance" : "Ve todo el embudo de un vistazo"}
              </h3>
              <p className="text-slate-500 leading-relaxed mb-6">
                {lang === "pt-BR" ? "Arraste deals entre etapas, configure campos vis\u00edveis por card e receba alertas de SLA automaticamente. M\u00faltiplos pipelines para produtos ou equipes diferentes." : lang === "en-US" ? "Drag deals between stages, configure visible fields per card and receive SLA alerts automatically. Multiple pipelines for different products or teams." : "Arrastra deals entre etapas, configura campos visibles por tarjeta y recibe alertas de SLA autom\u00e1ticamente."}
              </p>
              <ul className="space-y-2.5">
                {(lang === "pt-BR" ? ["M\u00faltiplos pipelines configur\u00e1veis","Campos vis\u00edveis por etapa do card","Alerta de SLA no card (prazo e inatividade)","Drag-and-drop com valida\u00e7\u00e3o de campos obrigat\u00f3rios"] : lang === "en-US" ? ["Multiple configurable pipelines","Visible fields per stage card","SLA alert on card (deadline and inactivity)","Drag-and-drop with required field validation"] : ["M\u00faltiples pipelines configurables","Campos visibles por etapa","Alerta de SLA en tarjeta","Drag-and-drop con validaci\u00f3n"]).map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-[#3B82F6] flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-1 order-1 lg:order-2">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                  <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-rose-400"/><div className="w-2.5 h-2.5 rounded-full bg-amber-400"/><div className="w-2.5 h-2.5 rounded-full bg-emerald-400"/></div>
                  <span className="text-[10px] text-slate-400 mx-auto">app.pipe4.com/deals</span>
                </div>
                <div className="p-4 bg-[#f8fafc]">
                  <div className="flex items-center justify-between mb-3">
                    <div><p className="text-xs font-bold text-slate-800">Pipeline Principal</p><p className="text-[10px] text-slate-400">4 etapas \u00b7 18 deals</p></div>
                    <div className="flex gap-1.5 text-[10px]">
                      <span className="bg-white border border-slate-200 px-2 py-1 rounded-lg text-slate-600">Kanban</span>
                      <span className="bg-white border border-slate-200 px-2 py-1 rounded-lg text-slate-400">Lista</span>
                    </div>
                  </div>
                  <div className="flex gap-3 overflow-x-auto pb-1">
                    {[
                      { name: "Potencial", color: "#ef4444", count: 5, deals: [
                        { t: "AutoGold Digital", v: "R$5k", p: "Gustavo M.", sla: false },
                        { t: "Frota Express", v: "R$12k", p: "Eduardo A.", sla: true },
                        { t: "MotoF\u00e1cil", v: "R$2k", p: "Carla F.", sla: false },
                      ]},
                      { name: "Qualificado", color: "#f97316", count: 4, deals: [
                        { t: "TechSales BR", v: "R$8k", p: "Gustavo M.", sla: false },
                        { t: "SaaS Connect", v: "R$15k", p: "Eduardo A.", sla: false },
                      ]},
                      { name: "Proposta", color: "#f59e0b", count: 6, deals: [
                        { t: "Mec\u00e2nica Central", v: "R$3k", p: "Carla F.", sla: true },
                        { t: "DataFlow BR", v: "R$22k", p: "Gustavo M.", sla: false },
                      ]},
                      { name: "Fechamento", color: "#10b981", count: 3, deals: [
                        { t: "NovaTech", v: "R$45k", p: "Eduardo A.", sla: false },
                      ]},
                    ].map(col => (
                      <div key={col.name} className="flex-shrink-0 w-36">
                        <div className="flex items-center gap-1.5 mb-2">
                          <div className="w-2 h-2 rounded-full" style={{backgroundColor:col.color}}/>
                          <p className="text-[10px] font-bold text-slate-700">{col.name}</p>
                          <span className="text-[9px] bg-slate-200 text-slate-500 px-1 rounded ml-auto">{col.count}</span>
                        </div>
                        <div className="space-y-1.5">
                          {col.deals.map((d,i) => (
                            <div key={i} className={`bg-white rounded-lg border p-2 shadow-sm ${d.sla ? "border-blue-200" : "border-slate-200"}`}>
                              {d.sla && <div className="flex items-center gap-1 text-[8px] text-blue-500 bg-blue-50 rounded px-1.5 py-0.5 mb-1 w-fit"><AlertCircle className="w-2 h-2"/>SLA</div>}
                              <p className="text-[9px] font-semibold text-slate-800 truncate">{d.t}</p>
                              <p className="text-[8px] text-slate-400 truncate">{d.p}</p>
                              <p className="text-[9px] font-bold text-[#3B82F6] mt-1">{d.v}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: WhatsApp */}
          <div className="flex flex-col lg:flex-row items-center gap-12 mb-24">
            <div className="flex-1">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                  <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-rose-400"/><div className="w-2.5 h-2.5 rounded-full bg-amber-400"/><div className="w-2.5 h-2.5 rounded-full bg-emerald-400"/></div>
                  <span className="text-[10px] text-slate-400 mx-auto">WhatsApp \u00b7 AutoGold Digital</span>
                </div>
                <div className="flex h-64">
                  <div className="flex-1 bg-[#ECE5DD] p-3 flex flex-col gap-2 overflow-hidden">
                    <div className="flex justify-start"><div className="bg-white rounded-xl rounded-tl-none px-3 py-2 shadow-sm max-w-[80%]"><p className="text-[10px] text-slate-700">Ol\u00e1! Recebi sua proposta, quando podemos conversar?</p><p className="text-[8px] text-slate-400 text-right mt-1">09:14</p></div></div>
                    <div className="flex justify-end"><div className="bg-[#DCF8C6] rounded-xl rounded-tr-none px-3 py-2 shadow-sm max-w-[80%]"><p className="text-[10px] text-slate-700">Oi Jo\u00e3o! Pode ser hoje \u00e0s 15h. Vou preparar os slides.</p><p className="text-[8px] text-slate-400 text-right mt-1">09:22 \u2713\u2713</p></div></div>
                    <div className="flex justify-start"><div className="bg-white rounded-xl rounded-tl-none px-3 py-2 shadow-sm max-w-[80%]"><p className="text-[10px] text-slate-700">Perfeito! Vejo voc\u00eas \u00e0s 15h ent\u00e3o \uD83D\uDC4D</p><p className="text-[8px] text-slate-400 text-right mt-1">09:24</p></div></div>
                    <div className="flex justify-end"><div className="bg-[#DCF8C6] rounded-xl rounded-tr-none px-3 py-2 shadow-sm max-w-[80%]"><p className="text-[10px] text-slate-700">\u00d3timo! At\u00e9 logo!</p><p className="text-[8px] text-slate-400 text-right mt-1">09:25 \u2713\u2713</p></div></div>
                    <div className="mt-auto flex items-center gap-2 bg-white rounded-full px-3 py-1.5 border border-slate-200">
                      <p className="text-[9px] text-slate-400 flex-1">Mensagem...</p>
                      <div className="w-5 h-5 bg-[#25D366] rounded-full flex items-center justify-center"><ArrowRight className="w-3 h-3 text-white"/></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <span className="inline-flex items-center gap-2 text-xs font-bold text-[#3B82F6] bg-blue-50 border border-blue-100 px-3 py-1 rounded-full mb-4">
                <MessageCircle className="w-3.5 h-3.5" />{lang === "pt-BR" ? "WhatsApp no Deal" : "WhatsApp in Deal"}
              </span>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                {lang === "pt-BR" ? "Converse com o cliente sem sair do CRM" : lang === "en-US" ? "Chat with the client without leaving the CRM" : "Habla con el cliente sin salir del CRM"}
              </h3>
              <p className="text-slate-500 leading-relaxed mb-6">
                {lang === "pt-BR" ? "Envie e receba mensagens WhatsApp diretamente na tela do neg\u00f3cio. Hist\u00f3rico completo sincronizado, badge de n\u00e3o lidas no Kanban e notifica\u00e7\u00f5es em tempo real." : lang === "en-US" ? "Send and receive WhatsApp messages directly on the deal screen. Full synced history, unread badge on Kanban and real-time notifications." : "Env\u00eda y recibe mensajes WhatsApp directamente en la pantalla del negocio."}
              </p>
              <ul className="space-y-2.5">
                {(lang === "pt-BR" ? ["Chat bidirecional dentro do deal","Hist\u00f3rico sincronizado automaticamente","Badge de n\u00e3o lidas no card do Kanban","Notifica\u00e7\u00e3o push no navegador"] : lang === "en-US" ? ["Two-way chat inside the deal","Automatically synced history","Unread badge on Kanban card","Browser push notification"] : ["Chat bidireccional dentro del negocio","Historial sincronizado autom\u00e1ticamente","Badge de no le\u00eddas en Kanban","Notificaci\u00f3n push en navegador"]).map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-[#3B82F6] flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Row 3: Dashboard & Reports */}
          <div className="flex flex-col lg:flex-row items-center gap-12 mb-24">
            <div className="flex-1 order-2 lg:order-1">
              <span className="inline-flex items-center gap-2 text-xs font-bold text-[#3B82F6] bg-blue-50 border border-blue-100 px-3 py-1 rounded-full mb-4">
                <BarChart2 className="w-3.5 h-3.5" />{lang === "pt-BR" ? "Dashboard & Relat\u00f3rios" : "Dashboard & Reports"}
              </span>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                {lang === "pt-BR" ? "Dados que realmente ajudam a decidir" : lang === "en-US" ? "Data that actually helps you decide" : "Datos que realmente ayudan a decidir"}
              </h3>
              <p className="text-slate-500 leading-relaxed mb-6">
                {lang === "pt-BR" ? "Dashboard com KPIs em tempo real, leaderboard de vendedores, forecast com probabilidade hist\u00f3rica por etapa e an\u00e1lise de motivos de perda." : lang === "en-US" ? "Dashboard with real-time KPIs, rep leaderboard, historical win-rate forecast per stage and loss reason analysis." : "Dashboard con KPIs en tiempo real, ranking de vendedores y pron\u00f3stico hist\u00f3rico por etapa."}
              </p>
              <ul className="space-y-2.5">
                {(lang === "pt-BR" ? ["MRR aberto, ganho e win rate em tempo real","Forecast ponderado por probabilidade hist\u00f3rica","Funil de convers\u00e3o por etapa","Relat\u00f3rio de motivos de perda com gr\u00e1fico"] : lang === "en-US" ? ["Open MRR, won MRR and win rate in real time","Forecast weighted by historical probability","Conversion funnel per stage","Loss reason report with chart"] : ["MRR abierto, ganado y win rate en tiempo real","Pron\u00f3stico ponderado por probabilidad hist\u00f3rica","Embudo de conversi\u00f3n por etapa","Informe de motivos de p\u00e9rdida"]).map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-[#3B82F6] flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex-1 order-1 lg:order-2">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                  <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-rose-400"/><div className="w-2.5 h-2.5 rounded-full bg-amber-400"/><div className="w-2.5 h-2.5 rounded-full bg-emerald-400"/></div>
                  <span className="text-[10px] text-slate-400 mx-auto">app.pipe4.com/dashboard</span>
                </div>
                <div className="p-4 bg-[#f8fafc] space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    {[{l:"OPEN MRR",v:"R$ 87k",s:"\u2191 18%",c:"#3B82F6"},{l:"WON MRR",v:"R$ 23k",s:"\u2191 40%",c:"#10b981"},{l:"WIN RATE",v:"56%",s:"12/21",c:"#f59e0b"}].map(k=>(
                      <div key={k.l} className="bg-white rounded-xl border border-slate-200 p-2.5 shadow-sm">
                        <p className="text-[8px] font-bold text-slate-400 uppercase mb-1">{k.l}</p>
                        <p className="text-base font-bold text-slate-900">{k.v}</p>
                        <p className="text-[9px] font-semibold" style={{color:k.c}}>{k.s}</p>
                      </div>
                    ))}
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
                    <p className="text-[10px] font-semibold text-slate-600 mb-2">Funil de Convers\u00e3o</p>
                    <div className="space-y-1.5">
                      {[["Potencial","85%","#ef4444","R$ 87k"],["Qualificado","62%","#f97316","R$ 54k"],["Proposta","41%","#f59e0b","R$ 31k"],["Fechamento","24%","#10b981","R$ 18k"]].map(([l,w,c,v])=>(
                        <div key={l} className="flex items-center gap-2">
                          <p className="text-[8px] text-slate-500 w-16 flex-shrink-0">{l}</p>
                          <div className="flex-1 bg-slate-100 rounded-full h-2"><div className="h-2 rounded-full" style={{width:w,backgroundColor:c}}/></div>
                          <p className="text-[8px] text-slate-500 w-10 text-right">{v}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
                    <p className="text-[10px] font-semibold text-slate-600 mb-2">Leaderboard</p>
                    {[{n:"Eduardo A.",mrr:"R$23k",deals:7,c:"#3B82F6"},{n:"Gustavo M.",mrr:"R$15k",deals:5,c:"#10b981"},{n:"Carla F.",mrr:"R$9k",deals:3,c:"#f59e0b"}].map((r,i)=>(
                      <div key={i} className="flex items-center gap-2 py-1 text-[9px]">
                        <div className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[7px] font-bold flex-shrink-0" style={{backgroundColor:r.c}}>{r.n[0]}</div>
                        <span className="flex-1 text-slate-700 font-medium truncate">{r.n}</span>
                        <span className="font-bold" style={{color:r.c}}>{r.mrr}</span>
                        <span className="text-slate-400">{r.deals} deals</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 4: Automations */}
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                  <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-rose-400"/><div className="w-2.5 h-2.5 rounded-full bg-amber-400"/><div className="w-2.5 h-2.5 rounded-full bg-emerald-400"/></div>
                  <span className="text-[10px] text-slate-400 mx-auto">app.pipe4.com/automations</span>
                </div>
                <div className="p-4 bg-[#f8fafc] space-y-3">
                  {[
                    { name: "Follow-up p\u00f3s SLA excedido", trigger: "SLA: Inatividade > 3 dias", action: "Criar tarefa + notificar", on: true },
                    { name: "Rotacionar lead por round-robin", trigger: "Neg\u00f3cio criado", action: "Rotacionar respons\u00e1vel", on: true },
                    { name: "WhatsApp ao ganhar neg\u00f3cio", trigger: "Neg\u00f3cio ganho", action: "Enviar WhatsApp + fechar tarefas", on: true },
                    { name: "Alerta de MRR alto qualificado", trigger: "Movido para Proposta", action: "Notifica\u00e7\u00e3o interna", on: false },
                  ].map((a, i) => (
                    <div key={i} className={`bg-white rounded-xl border p-3 flex items-center gap-3 shadow-sm ${a.on ? "border-slate-200" : "border-dashed border-slate-200 opacity-60"}`}>
                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${a.on ? "bg-blue-50" : "bg-slate-100"}`}>
                        <Zap className={`w-3.5 h-3.5 ${a.on ? "text-[#3B82F6]" : "text-slate-400"}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-semibold text-slate-800 truncate">{a.name}</p>
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          <span className="text-[8px] bg-blue-50 text-[#3B82F6] px-1.5 py-0.5 rounded font-medium">{a.trigger}</span>
                          <span className="text-[8px] text-slate-400">\u2192 {a.action}</span>
                        </div>
                      </div>
                      <div className={`w-7 h-4 rounded-full flex-shrink-0 ${a.on ? "bg-[#3B82F6]" : "bg-slate-200"}`}>
                        <div className={`w-3 h-3 bg-white rounded-full mt-0.5 transition-transform ${a.on ? "translate-x-3.5" : "translate-x-0.5"}`}/>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex-1">
              <span className="inline-flex items-center gap-2 text-xs font-bold text-[#3B82F6] bg-blue-50 border border-blue-100 px-3 py-1 rounded-full mb-4">
                <Zap className="w-3.5 h-3.5" />{lang === "pt-BR" ? "Automa\u00e7\u00f5es" : "Automations"}
              </span>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">
                {lang === "pt-BR" ? "Regras autom\u00e1ticas que trabalham enquanto voc\u00ea vende" : lang === "en-US" ? "Automatic rules that work while you sell" : "Reglas autom\u00e1ticas que trabajan mientras vendes"}
              </h3>
              <p className="text-slate-500 leading-relaxed mb-6">
                {lang === "pt-BR" ? "Configure automa\u00e7\u00f5es no formato if-this-then-that sem escrever uma linha de c\u00f3digo. Triggers baseados em SLA, movimenta\u00e7\u00e3o de etapa, ganhos, perdas e muito mais." : lang === "en-US" ? "Set up automations in if-this-then-that format without writing a single line of code. Triggers based on SLA, stage movement, wins, losses and more." : "Configura automatizaciones en formato if-this-then-that sin escribir c\u00f3digo."}
              </p>
              <ul className="space-y-2.5">
                {(lang === "pt-BR" ? ["Rotacionar respons\u00e1vel automaticamente","Criar tarefas ao mover entre etapas","Enviar WhatsApp ao ganhar neg\u00f3cio","Alertas por SLA excedido ou inatividade"] : lang === "en-US" ? ["Rotate assignee automatically","Create tasks when moving between stages","Send WhatsApp when deal is won","Alerts for exceeded SLA or inactivity"] : ["Rotar responsable autom\u00e1ticamente","Crear tareas al mover entre etapas","Enviar WhatsApp al ganar negocio","Alertas por SLA excedido"]).map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-[#3B82F6] flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* RESULTADOS REAIS */}
      {(() => {
        const r = RESULTS[lang];
        return (
          <section className="relative overflow-hidden bg-[#0f172a] py-20 sm:py-24" data-testid="landing-results">
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
            </div>
            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-14">
                <div className="inline-flex items-center gap-2 bg-blue-500/10 text-blue-400 text-xs font-semibold px-4 py-1.5 rounded-full border border-blue-500/20 mb-4">
                  <TrendingUp className="w-3.5 h-3.5" />{r.badge}
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold font-heading text-white mb-3">{r.title}</h2>
                <p className="text-slate-400 text-base max-w-xl mx-auto">{r.sub}</p>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
                {r.items.map((item, i) => (
                  <div key={i} className="relative bg-white/5 border border-white/10 rounded-2xl p-6 text-center hover:bg-white/8 hover:border-blue-500/30 transition-all group">
                    <div className="text-4xl sm:text-5xl font-extrabold text-white mb-2 tracking-tight group-hover:text-[#60a5fa] transition-colors">
                      {item.value}
                    </div>
                    <p className="text-sm font-semibold text-blue-400 mb-1">{item.label}</p>
                    <p className="text-xs text-slate-500 leading-snug">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* FEATURES */}
      <section id="recursos" className="py-20 sm:py-28 bg-slate-50 scroll-mt-16" data-testid="landing-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 mb-4">{c.features.title}</h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">{c.features.sub}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
            {c.features.items.map((feat, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all group">
                <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#3B82F6] transition-colors">
                  <feat.icon className="w-5 h-5 text-[#3B82F6] group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-2">{feat.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <button onClick={handleCTA}
              className="inline-flex items-center gap-2 px-7 py-3 border-2 border-[#3B82F6] text-[#3B82F6] rounded-xl font-semibold text-sm hover:bg-[#3B82F6] hover:text-white transition-all">
              {c.features.cta} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* PARA QUEM \u00c9 */}
      {(() => {
        const s = SEGMENTS[lang];
        return (
          <section className="py-20 sm:py-28 bg-white" data-testid="landing-segments">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-14">
                <div className="inline-flex items-center gap-2 bg-blue-50 text-[#3B82F6] text-xs font-semibold px-4 py-1.5 rounded-full border border-blue-100 mb-4">
                  <Target className="w-3.5 h-3.5" />{s.badge}
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 mb-4">{s.title}</h2>
                <p className="text-lg text-slate-500 max-w-2xl mx-auto">{s.sub}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {s.items.map((seg, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all bg-white group">
                    <div className="px-6 pt-5 pb-4 flex items-center gap-3 bg-slate-50 border-b border-slate-200 group-hover:bg-blue-50 transition-colors">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-white border border-slate-200 shadow-sm group-hover:border-blue-200 transition-colors">
                        {i === 0 && <Kanban    className="w-5 h-5 text-[#3B82F6]" />}
                        {i === 1 && <Building2 className="w-5 h-5 text-[#3B82F6]" />}
                        {i === 2 && <Megaphone className="w-5 h-5 text-[#3B82F6]" />}
                      </div>
                      <span className="text-sm font-bold text-slate-800">{seg.label}</span>
                    </div>
                    <div className="px-6 py-4 border-b border-slate-100">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2.5">
                        {lang === "pt-BR" ? "Dores comuns" : lang === "en-US" ? "Common pain points" : "Problemas comunes"}
                      </p>
                      <ul className="space-y-2">
                        {seg.pains.map((p, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                            <X className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />{p}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="px-6 py-4 bg-white">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#3B82F6] mb-2">
                        {lang === "pt-BR" ? "Como o Pipe4.me resolve" : lang === "en-US" ? "How Pipe4.me solves it" : "C\u00f3mo lo resuelve Pipe4.me"}
                      </p>
                      <p className="text-sm text-slate-600 leading-relaxed">{seg.solution}</p>
                    </div>
                    <div className="px-6 pb-5 bg-white">
                      <button onClick={handleCTA}
                        className="w-full py-2.5 rounded-xl text-sm font-semibold border-2 border-[#3B82F6] text-[#3B82F6] transition-all hover:bg-[#3B82F6] hover:text-white">
                        {seg.cta} \u2192
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* SOCIAL PROOF / QUEM SOMOS */}
      <section id="quem-somos" className="py-20 sm:py-28 bg-white scroll-mt-16" data-testid="landing-testimonials">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-blue-50 text-[#3B82F6] text-xs font-semibold px-4 py-1.5 rounded-full border border-blue-100 mb-4">
              <Star className="w-3.5 h-3.5 fill-current" />{c.testimonials.badge}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold font-heading text-slate-900 mb-4">{c.testimonials.title}</h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">{c.testimonials.sub}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {c.testimonials.items.map((t, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl border border-slate-200 p-6 flex flex-col gap-4 hover:shadow-md hover:-translate-y-0.5 transition-all">
                <div className="flex items-center justify-between">
                  <Stars />
                  <div className="text-right">
                    <p className="text-xl font-bold" style={{ color: t.color }}>{t.metric}</p>
                    <p className="text-[10px] text-slate-400 leading-tight">{t.metricLabel}</p>
                  </div>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed flex-1">"{t.quote}"</p>
                <div className="flex items-center gap-3 pt-2 border-t border-slate-200">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                    style={{ backgroundColor: t.color }}>{t.avatar}</div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.role} \u00b7 {t.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-8 sm:p-12 flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">{c.testimonials.about.title}</h3>
              <p className="text-slate-300 text-base leading-relaxed max-w-xl">{c.testimonials.about.desc}</p>
            </div>
            <button onClick={handleCTA}
              className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-3.5 bg-white text-slate-900 rounded-xl font-semibold text-sm hover:bg-slate-100 transition-all shadow-lg hover:-translate-y-0.5">
              {c.testimonials.about.cta} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
